import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import {
  FaTimes,
  FaUserShield,
  FaStore,
  FaUserTie,
  FaMagic,
  FaEye,
  FaEyeSlash,
  FaBoxOpen,
  FaIdCard,
} from "react-icons/fa";
import bcrypt from "bcryptjs";

const PartyModal = ({ party, onSave, onClose }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("profile"); // profile, security, permissions

  const [formData, setFormData] = useState({
    type: "Retailer",
    name: "",
    phone: "",
    email: "",
    gstNumber: "",
    address: "",
    username: "",
    password: "",
    status: "Active",
    // Future-proof settings object
    settings: {
      canViewCatalog: false,
      canModifyOrders: true,
      creditLimit: 0,
    },
  });

  useEffect(() => {
    if (party) {
      setFormData({
        type: party.type || "Retailer",
        name: party.name || "",
        phone: party.phone || "",
        email: party.email || "",
        gstNumber: party.gstNumber || "",
        address: party.address || "",
        username: party.username || "",
        password: "",
        status: party.status || "Active",
        settings: party.settings || {
          canViewCatalog: false,
          canModifyOrders: true,
          creditLimit: 0,
        },
      });
    }
  }, [party]);

  const generateUsername = () => {
    if (!formData.name) return alert("Please enter the Registered Name first.");
    const cleanName = formData.name.toLowerCase().replace(/[^a-z0-9]/g, "");
    const randomDigits = Math.floor(1000 + Math.random() * 9000);
    setFormData((prev) => ({
      ...prev,
      username: `${cleanName}_${randomDigits}`,
    }));
  };

  const generateSecurePassword = () => {
    const chars =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$*";
    let newPass = "";
    for (let i = 0; i < 10; i++)
      newPass += chars.charAt(Math.floor(Math.random() * chars.length));
    setFormData((prev) => ({ ...prev, password: newPass }));
    setShowPassword(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSettingChange = (e) => {
    const { name, checked, type, value } = e.target;
    const val = type === "checkbox" ? checked : value;
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [name]: val },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return alert("Entity Name is mandatory.");
    if (
      formData.type === "Retailer" &&
      !party &&
      (!formData.username || !formData.password)
    ) {
      return alert(
        "New Retailers require Portal Credentials (Username & Password).",
      );
    }

    setIsSaving(true);

    try {
      const submitData = { ...formData, updatedAt: serverTimestamp() };

      if (submitData.type === "Customer") {
        delete submitData.username;
        delete submitData.password;
        delete submitData.settings;
      } else if (submitData.type === "Retailer") {
        if (submitData.password) {
          const salt = bcrypt.genSaltSync(10);
          submitData.password = bcrypt.hashSync(submitData.password, salt);
        } else {
          delete submitData.password;
        }
      }

      if (party) {
        await updateDoc(doc(db, "parties", party.id), submitData);
      } else {
        submitData.createdAt = serverTimestamp();
        await addDoc(collection(db, "parties"), submitData);
      }
      onSave();
    } catch (error) {
      console.error("Error saving party:", error);
      alert("Database error occurred.");
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <div className="erp-modal-overlay">
      <div className="erp-modern-modal erp-pop-in party-modal-card">
        <div className="modal-header">
          <div className="header-info">
            <h2>{party ? "Update Party Profile" : "Onboard New Party"}</h2>
            <span>Configure access, catalogs, and primary data.</span>
          </div>
          <button className="close-btn" type="button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Scalable Tab Navigation */}
        <div className="modal-tabs">
          <button
            className={activeTab === "profile" ? "active" : ""}
            onClick={() => setActiveTab("profile")}
          >
            <FaIdCard /> Profile
          </button>
          {formData.type === "Retailer" && (
            <>
              <button
                className={activeTab === "security" ? "active" : ""}
                onClick={() => setActiveTab("security")}
              >
                <FaUserShield /> Security
              </button>
              <button
                className={activeTab === "permissions" ? "active" : ""}
                onClick={() => setActiveTab("permissions")}
              >
                <FaBoxOpen /> Permissions
              </button>
            </>
          )}
        </div>

        <form className="erp-modal-form" onSubmit={handleSubmit}>
          <div className="modal-body" style={{ minHeight: "350px" }}>
            {/* TAB: PROFILE */}
            {activeTab === "profile" && (
              <div className="fade-in">
                <div className="type-selector" style={{ marginBottom: "20px" }}>
                  <label
                    className={`radio-card ${formData.type === "Retailer" ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="Retailer"
                      checked={formData.type === "Retailer"}
                      onChange={handleChange}
                    />
                    <FaStore className="rc-icon" />
                    <div className="rc-text">
                      <strong>Retailer (B2B)</strong>
                      <span>Portal & Catalog access.</span>
                    </div>
                  </label>
                  <label
                    className={`radio-card ${formData.type === "Customer" ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value="Customer"
                      checked={formData.type === "Customer"}
                      onChange={handleChange}
                    />
                    <FaUserTie className="rc-icon" />
                    <div className="rc-text">
                      <strong>Direct Customer</strong>
                      <span>Walk-in client. No portal.</span>
                    </div>
                  </label>
                </div>

                <div className="erp-form-grid">
                  <div className="erp-input-group span-2">
                    <label>
                      Registered Name <span>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Business Name"
                    />
                  </div>
                  <div className="erp-input-group">
                    <label>Contact Number</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91..."
                    />
                  </div>
                  <div className="erp-input-group">
                    <label>GSTIN / Tax ID</label>
                    <input
                      type="text"
                      name="gstNumber"
                      value={formData.gstNumber}
                      onChange={handleChange}
                      placeholder="15-digit GSTIN"
                    />
                  </div>
                  <div className="erp-input-group span-2">
                    <label>Billing Address</label>
                    <textarea
                      name="address"
                      rows="2"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Complete physical address..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* TAB: SECURITY */}
            {activeTab === "security" && formData.type === "Retailer" && (
              <div className="fade-in erp-form-grid p-box">
                <div className="erp-input-group span-2">
                  <label>
                    Login Username <span>*</span>
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="Unique ID"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={generateUsername}
                      className="btn-icon-dark"
                    >
                      <FaMagic />
                    </button>
                  </div>
                </div>
                <div className="erp-input-group span-2">
                  <label>
                    {party ? "Reset Password" : "Login Password"} <span>*</span>
                  </label>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div style={{ position: "relative", flex: 1 }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder={
                          party
                            ? "Leave blank to keep current"
                            : "Set secure password"
                        }
                        style={{ width: "100%" }}
                      />
                      <span
                        onClick={() => setShowPassword(!showPassword)}
                        className="password-toggle-icon"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={generateSecurePassword}
                      className="btn-icon-dark"
                    >
                      <FaMagic />
                    </button>
                  </div>
                </div>
                <p className="credential-note span-2">
                  Passwords are mathematically hashed using bcrypt. Staff cannot
                  view passwords after they are saved.
                </p>
              </div>
            )}

            {/* TAB: PERMISSIONS & CATALOG */}
            {activeTab === "permissions" && formData.type === "Retailer" && (
              <div className="fade-in erp-form-grid">
                <div className="erp-input-group span-2">
                  <label
                    className="checkbox-label"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      cursor: "pointer",
                      background: "rgba(0,0,0,0.2)",
                      padding: "16px",
                      borderRadius: "8px",
                      border: "1px solid #334155",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="canViewCatalog"
                      checked={formData.settings.canViewCatalog}
                      onChange={handleSettingChange}
                      style={{ width: "20px", height: "20px" }}
                    />
                    <div>
                      <strong style={{ display: "block", color: "#f8fafc" }}>
                        Enable Stock Catalog Access
                      </strong>
                      <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                        Allow retailer to view ready jewelry stock and place
                        direct orders.
                      </span>
                    </div>
                  </label>
                </div>

                <div className="erp-input-group span-2">
                  <label
                    className="checkbox-label"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      cursor: "pointer",
                      background: "rgba(0,0,0,0.2)",
                      padding: "16px",
                      borderRadius: "8px",
                      border: "1px solid #334155",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="canModifyOrders"
                      checked={formData.settings.canModifyOrders}
                      onChange={handleSettingChange}
                      style={{ width: "20px", height: "20px" }}
                    />
                    <div>
                      <strong style={{ display: "block", color: "#f8fafc" }}>
                        Allow Order Modifications
                      </strong>
                      <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                        Retailer can edit specs while order is in "Pending"
                        state.
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="erp-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="erp-btn-primary"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Configuration"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default PartyModal;
